// src/features/gift-cards/pages/GiftCardConfigPage.tsx
// ============================================================================
// PÁGINA DE CONFIGURACIÓN DE GIFT CARDS — EXCLUSIVA PREMIUM
// ============================================================================
//
// Re-cableado a lealify-base (Inc12): la config vive en tenant_settings.giftcards
// (vía /api/giftcards/settings), NO en Firestore. El envío de prueba va por
// /api/giftcards/test (WhatsApp del negocio, shadow si no está conectado). Sin webhook n8n.
//
// Permite a usuarios PRO configurar:
//   1. Template del mensaje WhatsApp que acompaña la gift card
//   2. URL de Google Maps del negocio (para que el destinatario sepa dónde ir)
//   3. Toggle habilitado/deshabilitado
//   4. Enviar prueba a un número de teléfono
//
// Ubicación: src/features/gift-cards/pages/GiftCardConfigPage.tsx
// Ruta: /dashboard/gift-cards?tab=ajustes (F3 24-oct-2026; /dashboard/gift-card-config redirige aquí)
// Acceso: plan PRO o superior (isPro) — Premium fusionado en PRO (2026-06-25)
//
// Variables de template (estilo @, las que renderiza el backend):
//   @nombre    → Nombre del destinatario de la gift card
//   @remitente → Nombre de quien envía la gift card
//   @puntos    → Puntos / saldo regalado
//   @negocio   → Nombre del negocio
//   @direccion → URL de Google Maps
//
// ============================================================================

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import Layout from '../../../components/layout/Layout';
import PestanasModulo from '../../../components/common/PestanasModulo';
import { GIFT_TABS } from '../pestanas';
import BrandLoader from '../../../components/common/BrandLoader';
import { isPro as checkIsPremium } from '../../../utils/planPermissions';
import { ProLocked } from '../../../components/pro/ProLocked';
import {
  CreditCard,
  Save,
  Loader2,
  ToggleLeft,
  ToggleRight,
  Eye,
  Info,
  CheckCircle,
  AlertCircle,
  Sparkles,
  RotateCcw,
  Zap,
  MapPin,
  ExternalLink,
  Send,
} from 'lucide-react';
import { giftcardsApi, type GiftCardMode } from '../../../api/giftcardsApi';
import { whatsappApi } from '../../../api/whatsappApi';
import {
  generateGiftCardImage,
  toSendableImage,
  GIFT_CARD_TEMPLATES,
  type AnyGiftCardTemplateId,
  type GiftCardCustomDesign,
  type GiftCardTemplateInfo,
} from '../utils/giftCardTemplates';

// ============================================================================
// CONSTANTES
// ============================================================================

const DEFAULT_GIFT_CARD_TEMPLATE = '¡Hola @nombre! 🎁\n\n@remitente te ha regalado una Gift Card de @negocio con @puntos puntos.\n\n🎉 Visítanos y empieza a disfrutar tus recompensas.\n\n📍 ¿Dónde nos encuentras? @direccion\n\n¡Te esperamos con gusto! ⭐';

// ============================================================================
// TIPOS
// ============================================================================

interface GiftCardConfigData {
  enabled: boolean;
  mode: GiftCardMode;
  template: string;
  googleMapsUrl: string;
  updatedAt?: Date | null;
}

// Modo dinero (14-oct-2026, Marrieto #1400): mismo reemplazo que hace lealify-base (lib/giftcards
// renderMsg) — «@puntos puntos» no dice «$50 puntos», @monto/@puntos = «$50», @saldo_regalo = el saldo.
const conVariablesDinero = (msg: string, monto: string, saldo: string) => msg
  .replace(/@puntos\s+(puntos|pts)\b/gi, '@monto')
  .replace(/@saldo_regalo/g, saldo)
  .replace(/@monto/g, monto)
  .replace(/@puntos/g, monto);

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

const GiftCardConfigPage: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  // ── Permisos ──
  const hasPremiumAccess = checkIsPremium(currentUser?.subscription);
  const businessId = currentUser?.businessId;
  const loyaltyMode = currentUser?.loyaltyMode || 'visits';

  // ── Modo: qué regala la tarjeta (puntos o pesos de saldo de regalo) ──
  const [mode, setMode] = useState<GiftCardMode>('points');
  const enDinero = mode === 'money';

  // ── Variables disponibles ──
  const AVAILABLE_VARIABLES = useMemo(() => enDinero ? [
    { key: '@nombre', label: 'Nombre del destinatario', example: 'María' },
    { key: '@remitente', label: 'Quien envía la gift card', example: 'Ana García' },
    { key: '@monto', label: 'Pesos regalados', example: '$50' },
    { key: '@saldo_regalo', label: 'Saldo de regalo total del cliente', example: '$150' },
    { key: '@negocio', label: 'Nombre de tu negocio', example: currentUser?.businessName || 'Tu Negocio' },
    { key: '@direccion', label: 'URL de Google Maps', example: 'https://maps.google.com/...' },
  ] : [
    { key: '@nombre', label: 'Nombre del destinatario', example: 'María' },
    { key: '@remitente', label: 'Quien envía la gift card', example: 'Ana García' },
    { key: '@puntos', label: loyaltyMode === 'cashback' ? 'Saldo regalado' : 'Puntos regalados', example: loyaltyMode === 'cashback' ? '$50.00' : '50' },
    { key: '@negocio', label: 'Nombre de tu negocio', example: currentUser?.businessName || 'Tu Negocio' },
    { key: '@direccion', label: 'URL de Google Maps', example: 'https://maps.google.com/...' },
  ], [currentUser?.businessName, loyaltyMode, enDinero]);

  // ── Estado principal ──
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [template, setTemplate] = useState(DEFAULT_GIFT_CARD_TEMPLATE);
  const [googleMapsUrl, setGoogleMapsUrl] = useState('');
  const [originalConfig, setOriginalConfig] = useState<GiftCardConfigData | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // ── Estado WAHA ──
  const [wahaConnected, setWahaConnected] = useState<boolean | null>(null);
  const [isCheckingWaha, setIsCheckingWaha] = useState(false);

  // ── Preview del mensaje ──
  const [showPreview, setShowPreview] = useState(false);

  // ── Envío de prueba ──
  const [testPhone, setTestPhone] = useState('');
  const [testSending, setTestSending] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [testTemplateId, setTestTemplateId] = useState<AnyGiftCardTemplateId>('general');

  // ── Diseño propio del negocio (settings.custom_design; solo lectura aquí) ──
  const [customDesign, setCustomDesign] = useState<GiftCardCustomDesign | null>(null);

  // ============================================================================
  // CARGAR CONFIG EXISTENTE
  // ============================================================================

  useEffect(() => {
    if (!hasPremiumAccess || !businessId) {
      setLoading(false);
      return;
    }

    const loadConfig = async () => {
      setLoading(true);
      try {
        const { settings } = await giftcardsApi.getSettings();
        const isEnabled = settings.enabled === true;
        const tpl = settings.template || DEFAULT_GIFT_CARD_TEMPLATE;
        const mapsUrl = settings.google_maps_url || '';

        const md: GiftCardMode = settings.mode === 'money' ? 'money' : 'points';
        setEnabled(isEnabled);
        setMode(md);
        setTemplate(tpl);
        setGoogleMapsUrl(mapsUrl);
        setOriginalConfig({ enabled: isEnabled, mode: md, template: tpl, googleMapsUrl: mapsUrl, updatedAt: null });
        if (settings.custom_design?.enabled && settings.custom_design.background_url) {
          setCustomDesign(settings.custom_design);
          setTestTemplateId('custom');
        } else {
          setCustomDesign(null);
        }
      } catch (err) {
        console.error('Error cargando config de gift cards:', err);
        setError('No se pudo cargar la configuración.');
      } finally {
        setLoading(false);
      }
    };

    loadConfig();
  }, [hasPremiumAccess, businessId]);

  // ============================================================================
  // VERIFICAR WAHA
  // ============================================================================

  useEffect(() => {
    if (!hasPremiumAccess || !businessId) return;

    const checkWaha = async () => {
      setIsCheckingWaha(true);
      try {
        const result = await whatsappApi.status();
        setWahaConnected(result.connected === true);
      } catch {
        setWahaConnected(false);
      } finally {
        setIsCheckingWaha(false);
      }
    };

    checkWaha();
  }, [hasPremiumAccess, businessId]);

  // ============================================================================
  // PREVIEW DEL MENSAJE
  // ============================================================================

  const previewMessage = useMemo(() => {
    let msg = enDinero ? conVariablesDinero(template, '$50', '$150') : template;
    msg = msg.replace(/@nombre/g, 'María');
    msg = msg.replace(/@remitente/g, 'Ana García');
    msg = msg.replace(/@puntos/g, loyaltyMode === 'cashback' ? '$50.00' : '50');
    msg = msg.replace(/@negocio/g, currentUser?.businessName || 'Mi Negocio');
    msg = msg.replace(/@direccion/g, googleMapsUrl || 'https://maps.google.com/...');
    return msg;
  }, [template, currentUser?.businessName, googleMapsUrl, loyaltyMode, enDinero]);

  // ============================================================================
  // DETECTAR CAMBIOS
  // ============================================================================

  const hasChanges = useMemo(() => {
    if (!originalConfig) return false;
    return (
      enabled !== originalConfig.enabled ||
      mode !== originalConfig.mode ||
      template !== originalConfig.template ||
      googleMapsUrl !== originalConfig.googleMapsUrl
    );
  }, [enabled, mode, template, googleMapsUrl, originalConfig]);

  // ============================================================================
  // GUARDAR
  // ============================================================================

  const handleSave = useCallback(async () => {
    if (!businessId || !hasChanges) return;

    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      await giftcardsApi.updateSettings({
        enabled,
        // Solo si cambió: así un servidor sin la función de saldo de regalo no estorba el guardado.
        ...(originalConfig && mode !== originalConfig.mode ? { mode } : {}),
        template: template.trim(),
        google_maps_url: googleMapsUrl.trim(),
      });

      setOriginalConfig({
        enabled,
        mode,
        template: template.trim(),
        googleMapsUrl: googleMapsUrl.trim(),
        updatedAt: null,
      });
      setSuccessMessage('Configuración guardada correctamente.');
      setTimeout(() => setSuccessMessage(null), 4000);
    } catch (err: any) {
      console.error('Error guardando config de gift cards:', err);
      setError(err?.message ? `Error al guardar la configuración: ${err.message}` : 'Error al guardar la configuración.');
    } finally {
      setSaving(false);
    }
  }, [businessId, hasChanges, enabled, mode, originalConfig, template, googleMapsUrl]);

  // ============================================================================
  // RESETEAR AL TEMPLATE DEFAULT
  // ============================================================================

  const handleResetTemplate = useCallback(() => {
    setTemplate(DEFAULT_GIFT_CARD_TEMPLATE);
  }, []);

  // ============================================================================
  // ENVÍO DE PRUEBA
  // ============================================================================

  const handleTestSend = useCallback(async () => {
    if (!businessId || !testPhone.trim()) return;

    // Validar número
    let cleanPhone = testPhone.replace(/[\s\-\(\)\+]/g, '');
    if (cleanPhone.length === 10 && /^[2-9]/.test(cleanPhone)) {
      cleanPhone = '521' + cleanPhone;
    } else if (cleanPhone.length === 12 && cleanPhone.startsWith('52') && !cleanPhone.startsWith('521')) {
      cleanPhone = '521' + cleanPhone.substring(2);
    }

    if (!/^\d{10,15}$/.test(cleanPhone)) {
      setTestResult({ success: false, message: 'Número inválido. Ingresa un número de 10 dígitos (ej: 6861234567).' });
      return;
    }

    setTestSending(true);
    setTestResult(null);

    try {
      // 1. Generar imagen con el diseño seleccionado (data-URI; base la manda por sendImage)
      let imageBase64: string | undefined;
      try {
        imageBase64 = await toSendableImage(await generateGiftCardImage({
          templateId: testTemplateId,
          businessName: currentUser?.businessName || 'Mi Negocio',
          recipientName: 'Prueba',
          points: 50,
          primaryColor: currentUser?.businessColors?.primary || '#0891b2',
          logoUrl: currentUser?.logoUrl || null,
          // En pesos la tarjeta se dibuja como saldo («$50.00»), igual que en un negocio de cashback.
          loyaltyMode: enDinero ? 'cashback' : loyaltyMode,
          ...(enDinero ? { giftMoney: true } : {}),
          senderName: 'Equipo',
          customDesign: customDesign || undefined,
        })) || undefined;
      } catch (imgErr) {
        console.error('Error generando imagen de prueba:', imgErr);
      }

      // 2. Construir mensaje con las @-variables reemplazadas (lo que verá el destinatario)
      const pointsFormatted = loyaltyMode === 'cashback' ? '$50.00' : '50';
      let message = enDinero ? conVariablesDinero(template, '$50', '$50') : template;
      message = message.replace(/@nombre/g, 'Prueba');
      message = message.replace(/@remitente/g, 'Equipo');
      message = message.replace(/@puntos/g, pointsFormatted);
      message = message.replace(/@negocio/g, currentUser?.businessName || 'Mi Negocio');
      message = message.replace(/@direccion/g, googleMapsUrl || '');

      // 3. Enviar la prueba vía base (WhatsApp del negocio; shadow si no está conectado)
      const r = await giftcardsApi.testSend({ phone: cleanPhone, template: message, image: imageBase64 });

      if (r.ok) {
        setTestResult({
          success: true,
          message: r.shadow
            ? `Prueba registrada (modo demo: tu WhatsApp aún no está conectado).`
            : `Gift card de prueba enviada a ${testPhone}`,
        });
      } else {
        setTestResult({ success: false, message: `No se pudo enviar la prueba: ${r.detail || 'error desconocido'}` });
      }
    } catch (err: any) {
      console.error('Error enviando prueba:', err);
      setTestResult({ success: false, message: err?.message || 'Error al enviar la prueba.' });
    } finally {
      setTestSending(false);
    }
  }, [businessId, testPhone, template, googleMapsUrl, currentUser, loyaltyMode, testTemplateId, customDesign, enDinero]);

  // Diseños disponibles para la prueba (el propio del negocio primero, si existe)
  const testTemplates = useMemo<GiftCardTemplateInfo[]>(() => {
    if (!customDesign) return GIFT_CARD_TEMPLATES;
    return [
      {
        id: 'custom',
        label: `⭐ ${customDesign.label || 'Diseño de tu negocio'}`,
        description: 'El diseño propio de tu negocio',
        icon: '⭐',
        previewColors: ['#1c1917', customDesign.font?.color || '#e79cc2'],
      },
      ...GIFT_CARD_TEMPLATES,
    ];
  }, [customDesign]);

  // ============================================================================
  // GATE: NO PREMIUM
  // ============================================================================

  if (!hasPremiumAccess) {
    return (
      <Layout>
        <div className="space-y-6 px-2 sm:px-0">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-content flex items-center">
              <CreditCard className="h-5 w-5 sm:h-6 sm:w-6 mr-2 text-teal-500" />
              Gift Cards
            </h1>
          </div>
          <ProLocked feature="gift-cards" />
        </div>
      </Layout>
    );
  }

  // ============================================================================
  // LOADING
  // ============================================================================

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6 px-2 sm:px-0">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-content flex items-center">
              <CreditCard className="h-5 w-5 sm:h-6 sm:w-6 mr-2 text-teal-500" />
              Gift Cards
            </h1>
          </div>
          <PestanasModulo tabs={GIFT_TABS} activa="ajustes" testid="giftcards-tabs" />
          <div className="bg-surface shadow rounded-lg p-8 text-center">
            <BrandLoader size={36} />
            <p className="text-content-3 text-sm">Cargando configuración...</p>
          </div>
        </div>
      </Layout>
    );
  }

  // ============================================================================
  // CONTENIDO PRINCIPAL — Premium
  // ============================================================================

  const requirements = [
    {
      ok: wahaConnected === true,
      checking: isCheckingWaha,
      label: 'WhatsApp conectado',
      actionLabel: 'Vincular',
      actionPath: '/dashboard/whatsapp',
    },
    {
      ok: enabled,
      checking: false,
      label: 'Gift Cards activadas',
    },
    {
      ok: template.trim().length > 10,
      checking: false,
      label: 'Mensaje configurado',
    },
  ];

  const allReady = requirements.every(r => r.ok);

  return (
    <Layout>
      <div className="space-y-6 px-2 sm:px-0">
        {/* Header */}
        <div className="animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
          <h1 className="text-xl sm:text-2xl font-bold text-content flex items-center">
            <CreditCard className="h-5 w-5 sm:h-6 sm:w-6 mr-2 text-teal-500" />
            Gift Cards
          </h1>
          <p className="mt-1 text-sm text-content-2">
            Ajustes: el mensaje que acompaña la tarjeta, la ubicación de tu negocio y si el envío está activo
          </p>
        </div>
        <PestanasModulo tabs={GIFT_TABS} activa="ajustes" testid="giftcards-tabs" />

        {/* Checklist de requisitos */}
        <div className="bg-surface shadow rounded-lg p-4 sm:p-6 animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
          <div className="flex items-center mb-3">
            <Zap className="h-4 w-4 text-teal-500 mr-2" />
            <h3 className="text-sm font-semibold text-content">Requisitos para envío</h3>
          </div>
          <div className="space-y-2">
            {requirements.map((req, idx) => (
              <div key={idx} className="flex items-center justify-between text-sm">
                <div className="flex items-center">
                  {req.checking ? (
                    <Loader2 className="h-4 w-4 text-content-3 mr-2 animate-spin" />
                  ) : req.ok ? (
                    <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  ) : (
                    <AlertCircle className="h-4 w-4 text-content-3 mr-2" />
                  )}
                  <span className={req.ok ? 'text-content' : 'text-content-3'}>{req.label}</span>
                </div>
                {!req.ok && !req.checking && 'actionPath' in req && (
                  <button
                    onClick={() => navigate(req.actionPath as string)}
                    className="text-xs text-teal-600 dark:text-teal-400 hover:text-teal-800 dark:text-teal-300 font-medium"
                  >
                    {req.actionLabel}
                  </button>
                )}
              </div>
            ))}
          </div>
          {allReady && (
            <div className="mt-3 flex items-center text-xs text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-500/10 rounded-md px-3 py-2">
              <Sparkles className="h-3.5 w-3.5 mr-1.5" />
              Todo listo — las gift cards se enviarán por WhatsApp automáticamente
            </div>
          )}
        </div>

        {/* Card principal con toggle */}
        <div className="bg-surface shadow rounded-lg overflow-hidden animate-fade-in-up" style={{ animationDelay: '0.15s' }}>

          {/* Header con toggle */}
          <div className="px-4 sm:px-6 py-4 border-b border-line flex items-center justify-between">
            <div className="flex items-center">
              <CreditCard className="h-5 w-5 text-teal-500 mr-3" />
              <div>
                <h3 className="text-sm font-semibold text-content">
                  Gift Cards por WhatsApp
                </h3>
                <p className="text-xs text-content-3 mt-0.5">
                  Se enviará una imagen personalizada junto con tu mensaje
                </p>
              </div>
            </div>
            <button
              onClick={() => setEnabled(!enabled)}
              className="flex-shrink-0 ml-4"
              title={enabled ? 'Desactivar' : 'Activar'}
            >
              {enabled ? (
                <ToggleRight className="h-8 w-8 text-teal-500" />
              ) : (
                <ToggleLeft className="h-8 w-8 text-content-3" />
              )}
            </button>
          </div>

          <div className="p-4 sm:p-6 space-y-6">

            {/* ── Google Maps URL ── */}
            <div>
              <label className="block text-sm font-medium text-content-2 mb-1.5">
                <MapPin className="h-4 w-4 inline mr-1.5 text-teal-500" />
                URL de Google Maps
              </label>
              <p className="text-xs text-content-3 mb-2">
                Se incluirá en el mensaje para que el destinatario sepa dónde canjear su gift card
              </p>
              <div className="relative">
                <input
                  type="url"
                  value={googleMapsUrl}
                  onChange={(e) => setGoogleMapsUrl(e.target.value)}
                  placeholder="https://maps.google.com/..."
                  className="w-full border border-line rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 pr-10"
                />
                {googleMapsUrl && (
                  <a
                    href={googleMapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-content-3 hover:text-teal-600 dark:text-teal-400"
                    title="Abrir en Google Maps"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </a>
                )}
              </div>
            </div>

            {/* ── Qué regala la tarjeta (14-oct-2026, Marrieto #1400) ── */}
            <div data-testid="giftcard-mode">
              <label className="block text-sm font-medium text-content-2 mb-1.5">¿Qué regala la gift card?</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {([
                  ['points', 'Puntos', 'Se suman a los puntos del cliente y canjean recompensas (como siempre).'],
                  ['money', 'Pesos (saldo de regalo)', 'Un saldo en pesos aparte de los puntos. Se usa para PAGAR en tu caja Lealify con la forma de pago «Gift card»; no canjea recompensas ni vence.'],
                ] as [GiftCardMode, string, string][]).map(([k, t, d]) => (
                  <button key={k} type="button" onClick={() => setMode(k)}
                    className={`text-left rounded-lg border px-3 py-2.5 transition-colors ${mode === k
                      ? 'border-teal-500 bg-teal-50 dark:bg-teal-500/10' : 'border-line hover:bg-surface-2'}`}>
                    <span className="block text-sm font-semibold text-content">{t}</span>
                    <span className="block text-xs text-content-3 mt-0.5">{d}</span>
                  </button>
                ))}
              </div>
              {enDinero && (
                <p className="text-xs text-content-3 mt-2">
                  Necesitas la caja Lealify con la forma de pago «Gift card» encendida. Las gift cards que ya enviaste en puntos se quedan en puntos.
                  En el mensaje usa <b>@monto</b> (o @puntos, que en este modo sale en pesos).
                </p>
              )}
            </div>

            {/* ── Variables disponibles ── */}
            <div>
              <label className="block text-sm font-medium text-content-2 mb-2">
                Variables disponibles
              </label>
              <div className="flex flex-wrap gap-2">
                {AVAILABLE_VARIABLES.map((v) => (
                  <button
                    key={v.key}
                    onClick={() => {
                      const textarea = document.getElementById('gift-card-template') as HTMLTextAreaElement;
                      if (textarea) {
                        const start = textarea.selectionStart;
                        const end = textarea.selectionEnd;
                        const newText = template.slice(0, start) + v.key + template.slice(end);
                        setTemplate(newText);
                        setTimeout(() => {
                          textarea.focus();
                          textarea.setSelectionRange(start + v.key.length, start + v.key.length);
                        }, 0);
                      } else {
                        setTemplate((prev) => prev + v.key);
                      }
                    }}
                    className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-teal-50 dark:bg-teal-500/10 text-teal-700 dark:text-teal-300 hover:bg-teal-100 dark:hover:bg-teal-500/20 border border-teal-200 transition-colors"
                    title={`${v.label} — ejemplo: ${v.example}`}
                  >
                    {v.key}
                  </button>
                ))}
              </div>
            </div>

            {/* ── Textarea del template ── */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label htmlFor="gift-card-template" className="block text-sm font-medium text-content-2">
                  Mensaje de WhatsApp
                </label>
                <button
                  onClick={handleResetTemplate}
                  className="text-xs text-content-3 hover:text-teal-600 dark:text-teal-400 flex items-center"
                  title="Restaurar mensaje predeterminado"
                >
                  <RotateCcw className="h-3 w-3 mr-1" />
                  Predeterminado
                </button>
              </div>
              <textarea
                id="gift-card-template"
                value={template}
                onChange={(e) => setTemplate(e.target.value)}
                rows={6}
                className="w-full border border-line rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 resize-none"
                placeholder="Escribe tu mensaje personalizado..."
              />
              <p className="text-xs text-content-3 mt-1">
                {template.length} caracteres
              </p>
            </div>

            {/* ── Preview del mensaje ── */}
            <div>
              <button
                onClick={() => setShowPreview(!showPreview)}
                className="flex items-center text-sm text-teal-600 dark:text-teal-400 hover:text-teal-800 dark:text-teal-300 font-medium mb-2"
              >
                <Eye className="h-4 w-4 mr-1.5" />
                {showPreview ? 'Ocultar vista previa' : 'Ver vista previa del mensaje'}
              </button>
              {showPreview && (
                <div className="bg-surface-2 rounded-lg p-4 border border-line">
                  <div className="flex items-center mb-2">
                    <div className="h-8 w-8 bg-green-500 rounded-full flex items-center justify-center text-white text-xs font-bold mr-2">
                      WA
                    </div>
                    <div>
                      <p className="text-xs font-medium text-content">{currentUser?.businessName || 'Tu Negocio'}</p>
                      <p className="text-xs text-content-3">WhatsApp Business</p>
                    </div>
                  </div>
                  <div className="bg-surface rounded-lg p-3 shadow-sm border border-line">
                    <p className="text-sm text-content whitespace-pre-wrap">{previewMessage}</p>
                  </div>
                  <p className="text-xs text-content-3 mt-2 italic">
                    * Junto a este mensaje se enviará la imagen de gift card seleccionada al momento de crear la gift card
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* ── Mensajes de éxito/error ── */}
          {successMessage && (
            <div className="mx-4 sm:mx-6 mb-4 flex items-center text-sm text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-500/10 rounded-md px-3 py-2">
              <CheckCircle className="h-4 w-4 mr-2 flex-shrink-0" />
              {successMessage}
            </div>
          )}
          {error && (
            <div className="mx-4 sm:mx-6 mb-4 flex items-center text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-500/10 rounded-md px-3 py-2">
              <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* ── Footer con botón guardar ── */}
          <div className="px-4 sm:px-6 py-4 bg-surface-2 border-t border-line flex items-center justify-between">
            <div className="text-xs text-content-3">
              {originalConfig?.updatedAt
                ? `Última actualización: ${originalConfig.updatedAt.toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`
                : 'Sin configuración previa'}
            </div>
            <button
              onClick={handleSave}
              disabled={!hasChanges || saving}
              className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white transition-colors ${
                hasChanges && !saving
                  ? 'bg-teal-600 hover:bg-teal-700'
                  : 'bg-line text-content-3 cursor-not-allowed'
              }`}
            >
              {saving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              {saving ? 'Guardando...' : 'Guardar cambios'}
            </button>
          </div>
        </div>

        {/* ── Diseño propio del negocio (si está dado de alta) ── */}
        {customDesign && (
          <div className="bg-surface shadow rounded-lg overflow-hidden animate-fade-in-up" style={{ animationDelay: '0.12s' }}>
            <div className="px-4 sm:px-6 py-4 border-b border-line">
              <h3 className="text-sm font-semibold text-content flex items-center">
                <span className="mr-2">⭐</span>
                Diseño propio del negocio
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-100 text-green-700 dark:bg-green-500/10 dark:text-green-300">
                  ACTIVO
                </span>
              </h3>
            </div>
            <div className="p-4 sm:p-6 flex flex-col sm:flex-row gap-4 items-start">
              <img
                src={customDesign.background_url}
                alt={customDesign.label || 'Diseño del negocio'}
                className="w-full sm:w-64 rounded-lg border border-line shadow-sm"
              />
              <div className="text-sm text-content-2 space-y-2">
                <p className="font-medium text-content">{customDesign.label || 'Diseño de tu negocio'}</p>
                <p className="text-xs text-content-3">
                  Al enviar una gift card, este diseño aparece como opción ⭐ (preseleccionada) y se
                  llena solo con el nombre del destinatario, de parte de quién va y los puntos.
                </p>
                <p className="text-xs text-content-3">
                  ¿Quieres cambiar el arte o los textos fijos de la tarjeta? Contáctanos y lo
                  actualizamos por ti.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ── Envío de prueba ── */}
        {wahaConnected && (
          <div className="bg-surface shadow rounded-lg overflow-hidden animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
            <div className="px-4 sm:px-6 py-4 bg-gradient-to-r from-teal-50 to-cyan-50 dark:from-teal-500/10 dark:to-cyan-500/10">
              <h3 className="text-sm font-semibold text-content flex items-center">
                <Send className="h-4 w-4 mr-2 text-teal-500" />
                Enviar prueba
              </h3>
              <p className="mt-0.5 text-xs text-content-3">
                Envía una gift card de prueba para verificar cómo se ve en WhatsApp
              </p>
            </div>
            <div className="p-4 sm:p-6 space-y-4">
              
              {/* Selector de diseño para prueba */}
              <div>
                <label className="block text-sm font-medium text-content-2 mb-2">
                  Diseño de prueba
                </label>
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                  {testTemplates.map((tpl) => (
                    <button
                      key={tpl.id}
                      onClick={() => setTestTemplateId(tpl.id)}
                      className={`rounded-lg border-2 p-2 text-center transition-all ${
                        testTemplateId === tpl.id
                          ? 'border-teal-500 bg-teal-50 dark:bg-teal-500/10'
                          : 'border-line hover:border-line'
                      }`}
                    >
                      <div
                        className="h-8 rounded mb-1 flex items-center justify-center text-base"
                        style={{ background: `linear-gradient(135deg, ${tpl.previewColors[0]}, ${tpl.previewColors[1]})` }}
                      >
                        {tpl.icon}
                      </div>
                      <span className="text-[10px] font-medium text-content-2 leading-tight block">{tpl.label.replace(/^[^\s]+ /, '')}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Número */}
              <div>
                <label className="block text-sm font-medium text-content-2 mb-1">
                  Número de WhatsApp
                </label>
                <div className="flex items-center">
                  <span className="inline-flex items-center px-3 py-2 rounded-l-md border border-r-0 border-line bg-surface-2 text-content-3 text-sm">
                    MX
                  </span>
                  <input
                    type="tel"
                    value={testPhone}
                    onChange={(e) => { setTestPhone(e.target.value); setTestResult(null); }}
                    placeholder="6861234567"
                    className="flex-1 block w-full px-3 py-2 border border-line rounded-r-md shadow-sm text-sm focus:ring-teal-500 focus:border-teal-500"
                    maxLength={15}
                  />
                </div>
                <p className="mt-1 text-xs text-content-3">
                  Solo ingresa los 10 dígitos del número (sin 52, sin 1). Ejemplo: 6861234567
                </p>
              </div>

              {/* Resultado */}
              {testResult && (
                <div className={`flex items-center p-3 rounded-lg text-sm ${
                  testResult.success ? 'bg-green-50 dark:bg-green-500/10 text-green-700 dark:text-green-300' : 'bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-300'
                }`}>
                  {testResult.success ? (
                    <CheckCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                  ) : (
                    <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                  )}
                  {testResult.message}
                </div>
              )}

              {/* Botón enviar */}
              <button
                onClick={handleTestSend}
                disabled={testSending || !testPhone.trim()}
                className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
              >
                {testSending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                {testSending ? 'Enviando...' : 'Enviar gift card de prueba'}
              </button>
            </div>
          </div>
        )}

        {/* ── Cómo funciona ── */}
        <div className="bg-teal-50 dark:bg-teal-500/10 rounded-lg p-4 animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
          <div className="flex">
            <Info className="h-5 w-5 text-teal-500 mt-0.5 mr-3 flex-shrink-0" />
            <div className="text-sm text-teal-700 dark:text-teal-300 space-y-2">
              <p className="font-medium">¿Cómo funciona?</p>
              <p>
                Desde <strong>Gift Cards</strong> (grupo Programa del menú), pestaña Enviar, podrás crear gift cards
                ingresando el número de teléfono del destinatario, quién la envía, la cantidad de puntos
                y el diseño de la tarjeta. El sistema generará una imagen personalizada y enviará
                el mensaje por WhatsApp automáticamente.
              </p>
              <div className="flex items-start mt-2 space-x-4 text-xs">
                <div className="flex items-center">
                  <div className="h-6 w-6 rounded-full bg-teal-200 flex items-center justify-center text-teal-700 dark:text-teal-300 font-bold mr-2 text-xs">1</div>
                  <span>Destinatario y remitente</span>
                </div>
                <div className="flex items-center">
                  <div className="h-6 w-6 rounded-full bg-teal-200 flex items-center justify-center text-teal-700 dark:text-teal-300 font-bold mr-2 text-xs">2</div>
                  <span>Puntos y diseño</span>
                </div>
                <div className="flex items-center">
                  <div className="h-6 w-6 rounded-full bg-teal-200 flex items-center justify-center text-teal-700 dark:text-teal-300 font-bold mr-2 text-xs">3</div>
                  <span>WhatsApp enviado</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Nota sobre canjeo ── */}
        <div className="bg-amber-50 rounded-lg p-4 animate-fade-in-up" style={{ animationDelay: '0.15s' }}>
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-amber-500 mt-0.5 mr-3 flex-shrink-0" />
            <div className="text-sm text-amber-700">
              <p className="font-medium">Sobre el canjeo</p>
              <p className="mt-1">
                Si el destinatario ya es cliente, los puntos se suman inmediatamente. Si no lo es,
                los puntos se acreditarán automáticamente cuando se registre en tu kiosco con el
                mismo número de teléfono.
              </p>
            </div>
          </div>
        </div>

      </div>
    </Layout>
  );
};

export default GiftCardConfigPage;